import { TextClassContext } from '@/components/ui/text';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { cn } from '@/lib/utils';
import type { LucideIcon, LucideProps } from 'lucide-react-native';
import { cssInterop } from 'nativewind';
import * as React from 'react';
import { StyleSheet, type TextStyle } from 'react-native';

type IconStyle = TextStyle | TextStyle[] | null | undefined;

type IconProps = Omit<LucideProps, 'color' | 'style'> & {
  as: LucideIcon;
  className?: string;
  color?: string;
  strokeWidth?: number;
  style?: IconStyle;
};

function IconImpl({
  as: IconComponent,
  className: _className,
  color,
  strokeWidth,
  style,
  ...props
}: IconProps) {
  const forwardedProps = {
    ...props,
    ...(typeof strokeWidth === 'number' ? { strokeWidth } : null),
    ...(style ? { style } : null),
    ...(color ? { color } : null),
  } as unknown as LucideProps;

  return <IconComponent {...forwardedProps} />;
}

cssInterop(IconImpl, {
  className: {
    target: 'style',
    nativeStyleToProp: {
      height: 'size',
      width: 'size',
    },
  },
});

/**
 * A wrapper component for Lucide icons with Nativewind `className` support via `cssInterop`.
 *
 * This component allows you to render any Lucide icon while applying utility classes
 * using `nativewind`. It avoids the need to wrap or configure each icon individually.
 *
 * @component
 * @example
 * ```tsx
 * import { ArrowRight } from 'lucide-react-native';
 * import { Icon } from '@/registry/components/ui/icon';
 *
 * <Icon as={ArrowRight} className="text-red-500" size={16} />
 * ```
 *
 * @param {LucideIcon} as - The Lucide icon component to render.
 * @param {string} className - Utility classes to style the icon using Nativewind.
 * @param {number} size - Icon size (defaults to 14).
 * @param {...LucideProps} ...props - Additional Lucide icon props passed to the "as" icon.
 */
function Icon({ as: IconComponent, className, size = 14, ...props }: IconProps) {
  const textClass = React.useContext(TextClassContext);
  const flattenedStyle = React.useMemo(() => StyleSheet.flatten(props.style), [props.style]);
  const hasExplicitColor = React.useMemo(
    () =>
      Boolean(props.color || flattenedStyle?.color || hasExplicitTextColor([textClass, className])),
    [props.color, flattenedStyle?.color, textClass, className]
  );
  const resolvedColor = React.useMemo(() => {
    if (props.color) {
      return props.color;
    }

    if (typeof flattenedStyle?.color === 'string') {
      return flattenedStyle.color;
    }

    return hasExplicitColor ? undefined : DESIGN_TOKENS.colors.text;
  }, [flattenedStyle?.color, hasExplicitColor, props.color]);

  return (
    <IconImpl
      as={IconComponent}
      {...props}
      className={cn('text-foreground', textClass, className)}
      color={resolvedColor}
      size={size}
    />
  );
}

export { Icon };

function hasExplicitTextColor(classes: (string | undefined)[]) {
  const value = classes.filter(Boolean).join(' ');

  return /(^|\s)text-(foreground|muted-foreground|primary|primary-foreground|secondary-foreground|accent|accent-foreground|destructive|white|black|\[[^\]]+\])/.test(
    value
  );
}
